import { ref, shallowRef, type Component } from 'vue'

export interface SnackbarOptions {
  message: string
  title?: string
  // "loading" shows a spinner in place of the usual icon - for actions still in progress.
  type?: 'success' | 'error' | 'warning' | 'info' | 'loading'
  duration?: number
  dismissible?: boolean
  action?: {
    label: string
    callback: () => void
  }
}

export interface SnackbarItem extends SnackbarOptions {
  id: string
  visible: boolean
}

export function useSnackbar() {
  const snackbars = ref<SnackbarItem[]>([])
  const defaultDuration = 5000 // 5 seconds
  const maxVisibleSnackbars = 4

  const show = (options: SnackbarOptions | string) => {
    // Handle string input for simple messages
    const opts = typeof options === 'string'
      ? { message: options }
      : options

    const id = generateId()

    const snackbar: SnackbarItem = {
      id,
      message: opts.message,
      title: opts.title,
      type: opts.type || 'info',
      duration: opts.duration ?? defaultDuration,
      dismissible: opts.dismissible ?? true,
      action: opts.action,
      visible: true,
    }

    // Limit visible snackbars to maxVisibleSnackbars
    // Remove oldest visible snackbars if we're at the limit
    const visibleSnackbars = snackbars.value.filter(s => s.visible)
    if (visibleSnackbars.length >= maxVisibleSnackbars) {
      // Remove the oldest visible snackbar(s)
      const toRemove = visibleSnackbars.slice(0, visibleSnackbars.length - maxVisibleSnackbars + 1)
      toRemove.forEach(snackbar => close(snackbar.id))
    }

    // Add to snackbar list
    snackbars.value.push(snackbar)

    // Set auto-dismiss timer if duration > 0
    if (snackbar.duration > 0) {
      setTimeout(() => {
        close(id)
      }, snackbar.duration)
    }

    return id
  }

  const close = (id: string) => {
    const index = snackbars.value.findIndex(s => s.id === id)
    if (index !== -1) {
      // Set to invisible first (for animation)
      snackbars.value[index].visible = false

      // Then remove after animation completes
      setTimeout(() => {
        snackbars.value = snackbars.value.filter(s => s.id !== id)
      }, 300)
    }
  }

  const closeAll = () => {
    snackbars.value.forEach((snackbar) => {
      close(snackbar.id)
    })
  }

  // Shorthand methods for different types
  const success = (message: string, options: Partial<SnackbarOptions> = {}) =>
    show({ message, type: 'success', ...options })

  const error = (message: string, options: Partial<SnackbarOptions> = {}) =>
    show({ message, type: 'error', ...options })

  const warning = (message: string, options: Partial<SnackbarOptions> = {}) =>
    show({ message, type: 'warning', ...options })

  const info = (message: string, options: Partial<SnackbarOptions> = {}) =>
    show({ message, type: 'info', ...options })

  const loading = (message: string, options: Partial<SnackbarOptions> = {}) =>
    show({ message, type: 'loading', ...options })

  // Generate unique ID for snackbar
  const generateId = () => {
    return `snackbar-${Date.now()}-${Math.floor(Math.random() * 1000)}`
  }

  return {
    snackbars,
    show,
    success,
    error,
    warning,
    info,
    loading,
    close,
    closeAll,
  }
}

// Create a singleton instance
const snackbarService = useSnackbar()

export default snackbarService
